import {
	DEFAULT_COMMENTS_PER_CYCLE,
	DEFAULT_ITEMS_PER_ROW,
	DEFAULT_MAX_ROWS,
	DEFAULT_VIDEOS_PER_CYCLE,
} from './constants';

export type SupporterVoicesMix = {
	itemsPerRow?: number | null;
	maxRows?: number | null;
	commentsPerVideo?: number | null;
} | null;

export type SupporterVoicesFeedArgs = {
	maxItems: number;
	commentsPerCycle: number;
	videosPerCycle: number;
};

/**
 * The FCM is a freeform JSON config with no runtime validation, so its numeric fields aren't
 * guaranteed to be integers or within a sane range -- coerces to a safe non-negative integer
 * (rounding fractional values, falling back to `fallback` for anything missing or non-finite),
 * so a misconfigured FCM can't send GraphQL an invalid Int variable or a cycle size that stalls
 * the middle-end's interleaving.
 */
function toSafeInt(value: number | null | undefined, { min, fallback }: { min: number; fallback: number }): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
	return Math.max(Math.round(value), min);
}

/**
 * Derives the middle-end feed field's arguments from the FCM. `maxItems` reserves one slot for the
 * create-video CTA when it will render, so the CTA shares the last row instead of pushing that row
 * past the container width -- the middle-end has no notion of the CTA, so this must be computed
 * here rather than passed as itemsPerRow/maxRows and capped server-side.
 */
export function getSupporterVoicesFeedArgs({
	supporterVoicesMix,
	showCreateVideo,
}: {
	supporterVoicesMix: SupporterVoicesMix;
	showCreateVideo: boolean;
}): SupporterVoicesFeedArgs {
	const itemsPerRow = toSafeInt(supporterVoicesMix?.itemsPerRow, { min: 1, fallback: DEFAULT_ITEMS_PER_ROW });
	const maxRows = toSafeInt(supporterVoicesMix?.maxRows, { min: 1, fallback: DEFAULT_MAX_ROWS });
	const commentsPerCycle = toSafeInt(supporterVoicesMix?.commentsPerVideo, {
		min: 0,
		fallback: DEFAULT_COMMENTS_PER_CYCLE,
	});
	const maxItems = Math.max(itemsPerRow * maxRows - (showCreateVideo ? 1 : 0), 0);

	return { maxItems, commentsPerCycle, videosPerCycle: DEFAULT_VIDEOS_PER_CYCLE };
}
